/**
 * telefono.js — quando la cartella e' quella del medico al telefono
 * (proposta telefono-stile-nuovo, «Il metodo»; Alex, 28/10/2026, riferito).
 *
 * Il confine e' UNO: fino a 480 px compresi (Alex, 13/10/2026, T1: «fino a
 * 480 px»; dal 29/10 lo leggono anche il guscio e la pagina Oggi, che fino
 * ad allora avevano due confini, 600 e 480), con lo stile nuovo
 * «Console», per un utente collegato a un medico. Sopra, con lo stile di
 * prima o per gli altri ruoli, tutto com'e' oggi. Questa e' una scelta di
 * DISEGNO, non una regola di accesso: nessun «telefono» nelle rotte ne' nella
 * riservatezza (decisione 1 della proposta) - il server risponde al telefono
 * come al computer.
 */
import { createContext, useEffect, useState } from 'react';
import { stileNuovo } from './paginaIniziale';

/** L'ultima larghezza del telefono, compresa: da 481 px e' lo schermo grande. */
export const LARGHEZZA_TELEFONO = 480;
/** La stessa regola come media query: una copia sola, letta anche da pages/Oggi.jsx. */
export const DOMANDA_TELEFONO = `(max-width: ${LARGHEZZA_TELEFONO}px)`;
const DOMANDA = DOMANDA_TELEFONO;

function eTelefono() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(DOMANDA).matches;
}

/** Vero fino a 480 px compresi, e segue la rotazione o il ridimensionamento. */
export function useTelefono() {
    const [telefono, setTelefono] = useState(eTelefono);
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
        const mq = window.matchMedia(DOMANDA);
        const cambia = () => setTelefono(mq.matches);
        cambia();
        if (mq.addEventListener) mq.addEventListener('change', cambia);
        else if (mq.addListener) mq.addListener(cambia);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', cambia);
            else if (mq.removeListener) mq.removeListener(cambia);
        };
    }, []);
    return telefono;
}

/** La vista del medico da telefono: stile nuovo, medico collegato, schermo stretto. */
export const vistaTelefonoDelMedico = (utente, telefono) =>
    !!telefono && stileNuovo(utente) && !!utente?.medico_id;

/* ------------------------------------------------------------------
 * LA TESTATA E LA BARRA IN BASSO (proposta telefono-stile-nuovo, punto 4,
 * variante B; Alex, 28/10/2026, riferita). Qui solo le regole, senza
 * disegno: le prova navigazione/telefonoGuscio.test.js.
 * ------------------------------------------------------------------ */

/* I cinque posti della barra in basso, in quest'ordine. «Scatta» non e' una
   schermata: e' il gesto del punto 1 (CaricaDalTelefono), per il paziente
   della scheda aperta o per uno da scegliere. Le altre quattro sono righe
   della tabella delle schermate (navigazione/schermate.js): la barra non
   scrive ne' etichette ne' guardie sue. */
export const POSTI_BARRA = ['/oggi', '/agenda', 'scatta', '/pazienti', '/compiti'];

/**
 * I posti della barra per questo utente: { posto, schermata } con la riga
 * della tabella, o `schermata: null` se la rotta a quell'utente non si apre
 * (il posto resta vuoto, la barra non si accorcia: le voci non cambiano
 * posto da un ruolo all'altro). `apre` e' la domanda del menu (siApre),
 * passata da chi chiama perche' questo file non importi la tabella (che
 * importa le pagine, e le pagine importano questo file).
 */
export function postiBarra(utente, schermataDi, apre) {
    return POSTI_BARRA.map((posto) => {
        if (posto === 'scatta') return { posto, schermata: null, scatta: true };
        const s = schermataDi(posto);
        return { posto, schermata: s && apre(s, utente) ? s : null };
    });
}

const GIORNI_ESTESI = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
const MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio',
    'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];

/** «Mercoledì 28 ottobre» dal giorno della clinica «2026-10-28» (il giorno lo dice il server). */
export function giornoEsteso(giorno) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(giorno || ''));
    if (!m) return '';
    const w = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
    return `${GIORNI_ESTESI[w]} ${Number(m[3])} ${MESI[Number(m[2]) - 1]}`;
}

/**
 * La riga sotto il titolo: «Mercoledì 28 ottobre · 8 pazienti · 2 in
 * attesa». I numeri sono quelli che il server gia' conta in GET /api/oggi
 * nella vista «Le mie agende»: `riquadri.appuntamenti.n` (gli appuntamenti
 * di oggi senza annullati e non presentati) e `riquadri.sala.n` (in sala
 * d'attesa, IN_SALA_D_ATTESA). Qui non si conta niente: una parte che non
 * arriva non si scrive, invece di diventare uno zero.
 */
export function riepilogoGiornata(dati) {
    if (!dati) return '';
    const parti = [giornoEsteso(dati.giorno)];
    const n = dati.riquadri?.appuntamenti?.n;
    if (Number.isFinite(n)) parti.push(`${n} ${n === 1 ? 'paziente' : 'pazienti'}`);
    const s = dati.riquadri?.sala?.n;
    if (Number.isFinite(s)) parti.push(`${s} in attesa`);
    return parti.filter(Boolean).join(' · ');
}

/* Le fasi che non sono «pazienti di oggi»: le stesse che il server toglie da
   riquadri.appuntamenti.n (oggiController). */
const FUORI_DAL_GIORNO = ['annullato', 'non_presentato'];

/**
 * I pazienti di oggi per «Scatta» fuori da una scheda: le righe dell'agenda
 * della rotta, una per paziente (il primo appuntamento), senza annullati e
 * non presentati, nell'ordine della rotta.
 */
export function pazientiDiOggi(agenda) {
    const visti = new Set();
    const fuori = [];
    for (const r of agenda || []) {
        if (!r || !r.paziente_id || FUORI_DAL_GIORNO.includes(r.fase) || visti.has(r.paziente_id)) continue;
        visti.add(r.paziente_id);
        fuori.push(r);
    }
    return fuori;
}

/* «Scatta» dentro una scheda scatta PER QUEL PAZIENTE (punto 1). La scheda
   aperta lo dichiara registrando qui il suo gesto (CaricaDalTelefono con
   `ascoltaScatta`); la barra, al tocco, chiede prima a lei. Una sola alla
   volta: la scheda montata per ultima. Il gesto si chiama nello stesso
   tocco, senza attese: il ripiego sulla fotocamera del telefono e' un clic
   su un selettore, e il browser lo accetta solo dentro un gesto. */
let scattoRegistrato = null;

/** Registra il gesto di «Scatta» della scheda aperta; restituisce chi lo toglie. */
export function registraScatto(gesto) {
    scattoRegistrato = gesto;
    return () => { if (scattoRegistrato === gesto) scattoRegistrato = null; };
}

/** Scatta nella scheda aperta, se c'e': vero se qualcuno l'ha fatto. */
export function scattaNellaScheda() {
    if (typeof scattoRegistrato !== 'function') return false;
    scattoRegistrato();
    return true;
}

/* La giornata del medico per la testata (il riepilogo) e per «Scatta» (i
   pazienti di oggi): la risposta di GET /api/oggi?vista=mie. La tiene il
   Layout; la pagina «Oggi», che la chiede gia' per conto suo, gliela passa
   con questa funzione invece di farla chiedere due volte. Fuori dal Layout
   del telefono non c'e' nessuno ad ascoltare: la funzione di serie non fa
   niente. */
export const GiornataTelefono = createContext(() => {});

/* ------------------------------------------------------------------
 * LE LINGUETTE DELLA SCHEDA DEL PAZIENTE (proposta telefono-stile-nuovo,
 * punto 3; Alex, 30/10/2026: «com'e' costruita, variante B, fino a 480
 * px»). In una riga che NON scorre di lato: le prime quattro e «Altro», che
 * apre le altre. Le prime quattro sono le prime della cartella, nel suo
 * ordine (Riepilogo, Profilo medico, Visite e procedure, Documenti): la
 * proposta dice «le prime quattro», e l'ordine della cartella non cambia da
 * un dispositivo all'altro. Qui solo la regola; il disegno sta in
 * components/pazienti/LinguetteCartella.jsx.
 * ------------------------------------------------------------------ */

/** Quante linguette stanno nella riga del telefono prima di «Altro». */
export const PRIME_LINGUETTE_TELEFONO = 4;

/**
 * { riga, altro }: le prime `quante` linguette e le altre. Se le altre sono
 * una sola non serve «Altro»: sta nella riga anche lei (cinque posti, come
 * con «Altro»). Nessuna linguetta si perde e nessuna cambia ordine.
 */
export function divideLinguette(linguette, quante = PRIME_LINGUETTE_TELEFONO) {
    const tutte = Array.isArray(linguette) ? linguette : [];
    if (tutte.length <= quante + 1) return { riga: tutte, altro: [] };
    return { riga: tutte.slice(0, quante), altro: tutte.slice(quante) };
}
